/**
 * useCanvasRoom — 共有キャンバス（RT-05）のWebSocket接続を管理する
 *
 * サーバー実装（workers/anokoe/src/durable-objects/CanvasRoomDO.ts）のプロトコルに厳密に合わせる。
 *
 * 【ハマりどころを吸収している点】
 * 1. **チケットは単回使用**。再接続のたびに必ず取り直す（使い回すと初回だけ繋がり以降401）
 * 2. **WSのURLは API_BASE から組む**。本番は Pages(anokoe.jp) と Worker(app.anokoe.jp) が別オリジンで、
 *    相対パスだとローカルのviteプロキシでは動いて本番で落ちる（SYSTEM_SPEC §7.4 の既知の罠）
 * 3. **送信は合計30通/秒が上限**。ストロークは分割セグメントを50ms間隔で送出(=最大20/秒)、
 *    カーソルは200ms間隔(=最大5/秒)で、合計25/秒に配分（長い一筆でも rate_limited 切断に達しない）
 * 4. **1メッセージ200点まで**。超える線は分割し、継ぎ目が切れないよう前セグメント末尾の点を重複させる
 * 5. **履歴は done:true が唯一の終端**
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, api, CanvasStroke, CanvasParticipant } from '../services/api';

/** 接続状態（5状態すべて画面に出す） */
export type ConnState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

export type RoomError = { code: string; message: string } | null;

/** サーバー上限に合わせた送信設定 */
const MAX_POINTS_PER_MESSAGE = 200;
const STROKE_FLUSH_MS = 50;    // 最大20通/秒
const CURSOR_FLUSH_MS = 200;   // 最大5通/秒（合計25通/秒 < 30通/秒）
const MAX_RECONNECT = 5;       // 指数バックオフで最大5回。以降は手動（無限リトライ禁止）

export type OtherCursor = { userId: string; x: number; y: number; at: number };

export const useCanvasRoom = (roomId: string, enabled: boolean) => {
  const [state, setState] = useState<ConnState>('connecting');
  const [error, setError] = useState<RoomError>(null);
  const [strokes, setStrokes] = useState<CanvasStroke[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [participants, setParticipants] = useState<CanvasParticipant[]>([]);
  const [me, setMe] = useState<CanvasParticipant | null>(null);
  const [cursors, setCursors] = useState<Record<string, OtherCursor>>({});
  const [closedReason, setClosedReason] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  /**
   * 自分が描いた「一筆」単位のundo控え（自分の線しか戻せない）。
   * 200点超で分割された線はサーバー上は複数stroke（複数seq）になるが、
   * ユーザーにとっては1本なので、「ひとつ戻す」は分割セグメントを束ねて取り消す。
   * 1グループ = 1回の sendStroke。
   */
  const seqGroupsRef = useRef<Array<{ group: string; seqs: number[] }>>([]);
  /**
   * segment id → グループキー（分割前の stroke.id）。sendStroke で登録し、
   * echo/履歴の seq をグループへ振り分けるのに使う。welcome では消さない
   * （再接続後の履歴でも、このタブで描いた線は一筆単位で戻せるようにする）。
   * ページ再読込で消えた場合、過去の線は1セグメント=1グループに落ちる（安全側）。
   */
  const groupOfIdRef = useRef<Map<string, string>>(new Map());
  /**
   * 自分のuserId。`me` state はクロージャが張られた時点の値を掴んでしまい、
   * welcome 到着前は null のままになるため、ハンドラ内での判定には ref を使う
   * （これを state で判定すると自分の seq が記録されず undo が動かない）。
   */
  const myIdRef = useRef<string | null>(null);
  /** seq → stroke id の対応（undo でローカル先行描画分も確実に消すため） */
  const seqToIdRef = useRef<Map<number, string>>(new Map());
  /** 自分がローカル先行描画した stroke id（サーバーからの echo を二重描画しない） */
  const localIdsRef = useRef<Set<string>>(new Set());
  const cursorTimerRef = useRef<number | null>(null);
  const pendingCursorRef = useRef<{ x: number; y: number } | null>(null);

  /** 自分の seq をグループへ追加（直近グループと同じ一筆なら合流、違えば新グループ） */
  const pushMySeq = useCallback((seq: number, id: string) => {
    const group = groupOfIdRef.current.get(id) ?? id;
    const gs = seqGroupsRef.current;
    const last = gs[gs.length - 1];
    if (last && last.group === group) {
      if (!last.seqs.includes(seq)) last.seqs.push(seq);
    } else {
      gs.push({ group, seqs: [seq] });
    }
  }, []);

  const send = useCallback((payload: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  // ── 接続（チケットは毎回取り直す） ──
  const connect = useCallback(async (isRetry: boolean) => {
    if (!enabled || !roomId) return;
    manualCloseRef.current = false;
    setState(isRetry ? 'reconnecting' : 'connecting');
    setError(null);

    const t = await api.rtGetTicket(roomId);
    if (!t.success || !t.ticket) {
      // フラグOFF・閉室・未ログインなどはここで確定する
      setState('error');
      setError({
        code: t.code || 'TICKET_FAILED',
        message:
          t.code === 'FEATURE_DISABLED' ? '共有キャンバスはいま準備中です'
            : t.code === 'ROOM_CLOSED' ? 'この部屋は閉じました'
              : t.error || 'いま部屋に入れません',
      });
      return;
    }

    // 別オリジン対策: API_BASE から ws(s):// を組む（相対パスにしない）
    const base = API_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, 'ws') + `/api/rt/rooms/${roomId}/ws?ticket=${encodeURIComponent(t.ticket)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      setState('error');
      setError({ code: 'WS_FAILED', message: 'つながりませんでした' });
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setState('connected');
    };

    ws.onmessage = (ev) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }

      switch (msg.t) {
        case 'welcome':
          myIdRef.current = msg.you?.userId ?? null;
          setMe(msg.you);
          setParticipants(msg.participants || []);
          setHistoryLoading(true);
          setStrokes([]); // 再接続時は履歴で作り直す
          // 線を作り直す以上、線に紐づく控えも捨てる（古い seq / id が残ると undo が別の線を消す）
          // ※ groupOfIdRef だけは残す（履歴の再グループ化に使う。id→グループの対応は不変）
          seqGroupsRef.current = [];
          seqToIdRef.current.clear();
          localIdsRef.current.clear();
          break;

        case 'history':
          // done:true を唯一の終端条件にする（200件ちょうどでも固まらない）
          for (const s of (msg.strokes || [])) {
            // 再接続後も自分の線を取り消せるよう、履歴からも自分の seq を拾い直す
            if (s.authorId === myIdRef.current) pushMySeq(s.seq, s.id);
          }
          setStrokes(prev => [...prev, ...(msg.strokes || [])]);
          if (msg.done) setHistoryLoading(false);
          break;

        case 'stroke':
          // 自分の線は seq を控える（取り消しは自分の線だけ・id照合とは独立に判定する）
          if (msg.authorId === myIdRef.current) pushMySeq(msg.seq, msg.id);
          seqToIdRef.current.set(msg.seq, msg.id);
          setStrokes(prev => {
            // 自分がローカル先行描画した線は二重に描かない（送信時に登録した id と一致）
            if (localIdsRef.current.has(msg.id)) {
              localIdsRef.current.delete(msg.id);
              // seq を確定させるため、ローカル分を差し替える
              return prev.map(s => (s.id === msg.id ? { ...s, seq: msg.seq } : s));
            }
            return [...prev, msg as CanvasStroke];
          });
          break;

        case 'undo': {
          // seq で消す。ただしローカル先行描画分は echo 前だと seq が未確定(-1)なので、
          // 併せて「その seq に対応する id」でも消す（seq→id の対応は stroke 受信時に控える）
          const removedId = seqToIdRef.current.get(msg.seq);
          setStrokes(prev => prev.filter(s => s.seq !== msg.seq && (!removedId || s.id !== removedId)));
          for (const g of seqGroupsRef.current) g.seqs = g.seqs.filter(s => s !== msg.seq);
          seqGroupsRef.current = seqGroupsRef.current.filter(g => g.seqs.length > 0);
          seqToIdRef.current.delete(msg.seq);
          break;
        }

        case 'cursor':
          setCursors(prev => ({ ...prev, [msg.userId]: { userId: msg.userId, x: msg.x, y: msg.y, at: Date.now() } }));
          break;

        case 'join':
          setParticipants(prev => prev.some(p => p.userId === msg.participant.userId) ? prev : [...prev, msg.participant]);
          break;

        case 'leave':
          setParticipants(prev => prev.filter(p => p.userId !== msg.userId));
          setCursors(prev => { const n = { ...prev }; delete n[msg.userId]; return n; });
          break;

        case 'closed':
          manualCloseRef.current = true; // 閉室は再接続しない
          setClosedReason(msg.reason || 'closed');
          setState('disconnected');
          break;

        case 'error':
          setError({ code: msg.code, message: msg.message || '' });
          break;

        case 'pong':
        default:
          break;
      }
    };

    ws.onerror = () => {
      // onclose も続けて来るので、ここでは状態だけ落とす
      if (!manualCloseRef.current) setState('error');
    };

    ws.onclose = (ev) => {
      wsRef.current = null;
      if (manualCloseRef.current) { setState('disconnected'); return; }
      // サーバーが明示的に断った(1008: rate_limited / no_attachment)場合は、
      // チケットを取り直して投げ直しても同じ結果になるのでリトライしない。
      // ※room_full(409)/too_many_connections(429) は upgrade 自体が失敗して 1006 になり
      //   ネットワーク断と区別できないため、そちらは下の最大5回に任せる
      if (ev.code === 1008) {
        setState('disconnected');
        setError(prev => prev ?? {
          code: ev.reason || 'REJECTED',
          message: ev.reason === 'rate_limited' ? '送信が多すぎたため、いったん切断されました' : 'いま接続できませんでした',
        });
        return;
      }
      // 指数バックオフで最大5回。以降は手動の「もう一度つなぐ」に委ねる
      if (attemptRef.current < MAX_RECONNECT) {
        const delay = Math.min(8000, 500 * 2 ** attemptRef.current);
        attemptRef.current++;
        setState('reconnecting');
        window.setTimeout(() => { void connect(true); }, delay);
      } else {
        setState('disconnected');
      }
    };
    // me は ref（myIdRef）で参照するため依存に入れない（入れると welcome のたびに再接続する）
  }, [roomId, enabled]);

  useEffect(() => {
    void connect(false);
    return () => {
      manualCloseRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      if (cursorTimerRef.current) window.clearInterval(cursorTimerRef.current);
    };
    // roomId が変わったときだけ張り直す（connect の依存で再接続ループにしない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, enabled]);

  // ── カーソルは200msに1回だけ送る（レート配分） ──
  useEffect(() => {
    cursorTimerRef.current = window.setInterval(() => {
      const p = pendingCursorRef.current;
      if (!p) return;
      pendingCursorRef.current = null;
      send({ t: 'cursor', x: p.x, y: p.y });
    }, CURSOR_FLUSH_MS);
    return () => { if (cursorTimerRef.current) window.clearInterval(cursorTimerRef.current); };
  }, [send]);

  /** カーソル位置の予約（実送信は200ms間隔） */
  const sendCursor = useCallback((x: number, y: number) => {
    pendingCursorRef.current = { x, y };
  }, []);

  /**
   * ストロークを送る。**ローカル先行描画までここで行う**（呼び出し側は id を意識しない）。
   *
   * 200点を超える線は分割し、**前セグメントの最終点を次セグメントの先頭に重複**させて
   * 継ぎ目が切れないようにする。分割で id が `${id}-0` のように変わるため、
   * ローカル先行描画も**送るのと同じ id・同じ分割単位**で行う必要がある。
   * （別々にすると echo が新しい線として増えて二重に描かれ、undo でも元の線が消え残る）
   */
  const sendStroke = useCallback((stroke: CanvasStroke) => {
    const points = stroke.points || [];
    if (points.length === 0) return;

    // 送る単位＝ローカルに置く単位。分割の判断はこの1箇所だけ
    // 分割してもユーザーにとっては1本 → 全セグメントを元の id のグループに登録（undo用）
    const segments: CanvasStroke[] = [];
    if (points.length <= MAX_POINTS_PER_MESSAGE) {
      segments.push(stroke);
    } else {
      let start = 0;
      let part = 0;
      while (start < points.length) {
        const end = Math.min(start + MAX_POINTS_PER_MESSAGE, points.length);
        segments.push({ ...stroke, id: `${stroke.id}-${part}`, points: points.slice(start, end) });
        part++;
        if (end >= points.length) break;
        start = end - 1; // 継ぎ目の重複
      }
    }

    for (const seg of segments) {
      localIdsRef.current.add(seg.id);
      groupOfIdRef.current.set(seg.id, stroke.id);
    }
    setStrokes(prev => [...prev, ...segments]); // 送信を待たず自分の画面には即出す
    // 分割セグメントは50ms間隔で送出する（サーバー上限30通/秒に対し最大20通/秒に抑える）。
    // 一括送出だと、200点超の長い一筆（例: 数分間ペンを離さないシェーディング）で
    // セグメント数が上限を超えた瞬間に rate_limited(1008) で切断される。
    // WebSocketは送信順を保証するため、間隔を空けても線の順序は崩れない。
    segments.forEach((seg, i) => {
      const payload = { t: 'stroke', id: seg.id, tool: seg.tool, color: seg.color, width: seg.width, points: seg.points };
      if (i === 0) { send(payload); return; }
      window.setTimeout(() => { send(payload); }, i * STROKE_FLUSH_MS);
    });
  }, [send]);

  /**
   * 直近の自分の「一筆」を取り消す（自分の seq のみ送る）。
   * 分割された線は全セグメントをまとめて取り消す（「ひとつ戻す」＝一筆単位）。
   * 複数seqの送出は50ms間隔に抑える（レート上限30通/秒への配慮）。
   */
  const undoMine = useCallback(() => {
    const gs = seqGroupsRef.current;
    const last = gs[gs.length - 1];
    if (!last || last.seqs.length === 0) return false;
    // 覗くだけにして、**送れたときだけ**捨てる。
    // 先に pop すると切断中に押した線が二度と取り消せなくなる
    const seqs = [...last.seqs];
    if (!send({ t: 'undo', seq: seqs[0] })) return false;
    gs.pop();
    seqs.slice(1).forEach((seq, i) => {
      window.setTimeout(() => { send({ t: 'undo', seq }); }, (i + 1) * STROKE_FLUSH_MS);
    });
    return true;
  }, [send]);

  /** 手動再接続（新しいチケットを取り直す） */
  const reconnect = useCallback(() => {
    attemptRef.current = 0;
    setClosedReason(null);
    void connect(true);
  }, [connect]);

  return {
    state, error, strokes, historyLoading, participants, me, cursors, closedReason,
    sendStroke, sendCursor, undoMine, reconnect,
    canUndo: () => seqGroupsRef.current.length > 0,
  };
};
